"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useIsPortalEdit = exports.useIsPortalDraft = exports.usePortalMetricsActionsComponents = exports.usePortalMetadata = exports.useIsAssetViewer = exports.useIsAssetContributor = exports.useIsAssetAdmin = exports.useVersionsTableDisabled = exports.useVersionsTable = exports.usePortalActions = exports.useFormConfigurations = exports.useVersioningAllowance = exports.usePortalVisibility = exports.useAssetFiles = exports.usePortalRoutes = exports.useAsset = void 0;
const react_1 = __importDefault(require("react"));
const contexts_1 = require("../contexts");
const withAssetPortal_1 = require("../contexts/withAssetPortal");
const config_1 = require("./config");
const user_1 = require("./user");
const router_1 = require("./router");
/**
 * When navigating a portal, gets the current asset being shown.
 *
 * @example
 *
 * ```tsx
 * import { useAsset } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const asset = useAsset();
 *
 *   return asset.type;
 * };
 * ```
 *
 * @returns The asset
 */
function useAsset() {
    const assetPortalContext = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    return assetPortalContext === null || assetPortalContext === void 0 ? void 0 : assetPortalContext.asset;
}
exports.useAsset = useAsset;
/**
 * Return routes configured for the current asset portal type.
 *
 * @example
 *
 * ```tsx
 * import { usePortalRoutes } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const routes = usePortalRoutes();
 *
 *   return `seeing ${routes.length} routes`;
 * };
 * ```
 *
 * @returns An array with the routes for the portal
 */
function usePortalRoutes() {
    var _a;
    const assetPortalContext = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    return (_a = assetPortalContext === null || assetPortalContext === void 0 ? void 0 : assetPortalContext.routes) !== null && _a !== void 0 ? _a : [];
}
exports.usePortalRoutes = usePortalRoutes;
const emptyFilesConfig = { files: [] };
/**
 * When navigating a portal, gets the files for the asset being shown.
 *
 * @example
 *
 * ```tsx
 * import { useAssetFiles } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const { files } = useAssetFiles();
 *
 *   return `seeing ${files.length} files`;
 * };
 * ```
 *
 * @param section The section of the portal where the files are going to be showed
 * @returns An object representing the files for the target section
 */
function useAssetFiles(section) {
    const asset = useAsset();
    const config = (0, config_1.useConfig)();
    const assetPortalContext = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    const studioMetadata = (0, user_1.useStudioMetadata)();
    if (assetPortalContext === null || assetPortalContext === void 0 ? void 0 : assetPortalContext.files) {
        return assetPortalContext.files(asset, studioMetadata, section, config);
    }
    return emptyFilesConfig;
}
exports.useAssetFiles = useAssetFiles;
/**
 * Returns the visibility of the asset: `private` or `public`.
 * If the visibility is `private`, then this portal cannot be marked as public
 * If the visibility is `public` then this portal can be marked as public
 *
 * @example
 *
 * ```tsx
 * import { usePortalVisibility } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const portalVisibility = usePortalVisibility();
 *
 *   ...
 * };
 * ```
 *
 * @returns The available visibility for the portal
 */
function usePortalVisibility() {
    const assetPortalContext = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    return assetPortalContext.visibility;
}
exports.usePortalVisibility = usePortalVisibility;
/**
 * Returns the versioning strategy for the current asset when navigating an asset portal.
 *
 * Some examples:
 * HTTP API and API Groups have "major" as allowance, that means that only major bumps can be published (1.0.0, 2.0.0, etc)
 * Other types have "patch" as allowance, so any valid semver value can be published
 *
 * @example
 *
 * ```tsx
 * import { useVersioningAllowance } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const versioningAllowance = useVersioningAllowance();
 *
 *   ...
 * };
 * ```
 *
 * @returns The versioning allowance for this asset
 */
function useVersioningAllowance() {
    var _a, _b;
    const assetTypesConfiguration = react_1.default.useContext(contexts_1.AssetTypesConfigurationContext);
    const asset = useAsset();
    const versioningAllowance = (_b = (_a = assetTypesConfiguration[asset.type]) === null || _a === void 0 ? void 0 : _a.versioningAllowance) !== null && _b !== void 0 ? _b : 'patch';
    return versioningAllowance;
}
exports.useVersioningAllowance = useVersioningAllowance;
/**
 * Returns an array of available configurations for the publish form.
 *
 * @example
 *
 * ```tsx
 * import { useFormConfigurations } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const formConfigurations = useFormConfigurations();
 *
 *   ...
 * };
 * ```
 *
 * @param latestAsset When creating a new version for an existing asset, this parameter represents the latest version for the given asset
 * @param types Fixed list of types to have available in the form
 * @returns An array of possible configurations
 */
function useFormConfigurations(latestAsset, types) {
    const assetTypesConfiguration = react_1.default.useContext(contexts_1.AssetTypesConfigurationContext);
    const config = (0, config_1.useConfig)();
    const profile = (0, user_1.useProfile)();
    const formConfigurations = react_1.default.useMemo(() => Object.keys(assetTypesConfiguration).reduce((accum, current) => {
        const assetTypeConfiguration = assetTypesConfiguration[current];
        const getFormConfiguration = assetTypeConfiguration.form;
        const isFixedType = types === null || types === void 0 ? void 0 : types.includes(current);
        if (!getFormConfiguration) {
            return accum;
        }
        if (types && !isFixedType) {
            return accum;
        }
        if (current === 'default') {
            return accum;
        }
        if (assetTypeConfiguration.featureFlag &&
            !config.featureFlags[assetTypeConfiguration.featureFlag]) {
            return accum;
        }
        const formConfiguration = getFormConfiguration({
            config,
            latestAsset,
            profile
        });
        if (!formConfiguration) {
            return accum;
        }
        if (formConfiguration.hidden && !isFixedType) {
            return accum;
        }
        const typeFlows = Object.assign({ type: current, name: assetTypeConfiguration.name }, formConfiguration);
        if (typeFlows.flows.length === 0) {
            return accum;
        }
        return [...accum, typeFlows];
    }, []), [config, profile, latestAsset, types, assetTypesConfiguration]);
    return formConfigurations;
}
exports.useFormConfigurations = useFormConfigurations;
/**
 * Returns a Portal Actions
 *
 * @example
 *
 * ```ts
 * import { usePortalActions } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = ({ ...props }) => {
 *   const Actions = usePortalActions();
 *   <Actions asset={asset}/>
 *   ...
 * };
 * ```
 *
 * @returns A React component that represents the portal actions
 */
const usePortalActions = () => {
    const { components: { Actions } } = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    return Actions;
};
exports.usePortalActions = usePortalActions;
/**
 * Returns a Version Table component
 *
 * @example
 *
 * ```ts
 * import { useVersionsTable } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = ({ ...props }) => {
 *   const VersionsTable = useVersionsTable();
 *   <VersionsTable asset={asset}/>
 *   ...
 * };
 * ```
 *
 * @returns A React component that represents the portal versions table
 */
const useVersionsTable = () => {
    const { components: { VersionsTable } } = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    return VersionsTable;
};
exports.useVersionsTable = useVersionsTable;
/**
 * Returns true if the versions table is disabled for this portal
 *
 * @example
 *
 * ```ts
 * import { useVersionsTableDisabled } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = ({ ...props }) => {
 *   const isVersionsTableDisabled = useVersionsTableDisabled();
 *   ...
 * };
 * ```
 *
 * @returns A boolean that will be true if the versions table is disabled in the portal
 */
const useVersionsTableDisabled = () => {
    const { isVersionsTableDisabled } = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    return isVersionsTableDisabled;
};
exports.useVersionsTableDisabled = useVersionsTableDisabled;
/**
 * Returns true if the user is an admin
 *
 * @returns A boolean that will be true if the user is an admin of the asset
 *          and is not in the public portal or in studio
 */
const useIsAssetAdmin = () => {
    var _a;
    const asset = useAsset();
    const isPublicPortal = (0, router_1.useIsPublicPortal)();
    const isStudio = (0, user_1.useIsStudio)();
    if (isPublicPortal || isStudio) {
        return false;
    }
    return (_a = asset === null || asset === void 0 ? void 0 : asset.permissions) === null || _a === void 0 ? void 0 : _a.includes('admin');
};
exports.useIsAssetAdmin = useIsAssetAdmin;
/**
 * Returns true if the user is an contributor
 *
 * @returns A boolean that will be true if the user is an contributor of the asset
 *          and is not in the public portal or in studio
 */
const useIsAssetContributor = () => {
    var _a;
    const asset = useAsset();
    const isPublicPortal = (0, router_1.useIsPublicPortal)();
    const isStudio = (0, user_1.useIsStudio)();
    if (isPublicPortal || isStudio) {
        return false;
    }
    return (_a = asset === null || asset === void 0 ? void 0 : asset.permissions) === null || _a === void 0 ? void 0 : _a.includes('edit');
};
exports.useIsAssetContributor = useIsAssetContributor;
/**
 * Returns true if the user is an viewer
 *
 * @returns A boolean that will be true if the user is an viewer of the asset
 */
const useIsAssetViewer = () => {
    var _a;
    const asset = useAsset();
    return (_a = asset === null || asset === void 0 ? void 0 : asset.permissions) === null || _a === void 0 ? void 0 : _a.includes('view');
};
exports.useIsAssetViewer = useIsAssetViewer;
/**
 * Returns the additional metadata for the portal for the current asset type. For example:
 *   - REST APIs have instances and governance badge as additional metadata
 *   - Connectors and examples have the runtime as additional metadata
 *
 * @param section The section where the metadata is going to be shown
 * @returns An array with the metadata for the portal for the current asset type
 */
const usePortalMetadata = (section) => {
    const assetPortalConfiguration = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    const asset = useAsset();
    const config = (0, config_1.useConfig)();
    const metadata = react_1.default.useMemo(() => {
        var _a, _b;
        return (_b = (_a = assetPortalConfiguration === null || assetPortalConfiguration === void 0 ? void 0 : assetPortalConfiguration.metadata) === null || _a === void 0 ? void 0 : _a.call(assetPortalConfiguration, {
            asset,
            config,
            section
        })) !== null && _b !== void 0 ? _b : [];
    }, [assetPortalConfiguration, asset, config, section]);
    return metadata;
};
exports.usePortalMetadata = usePortalMetadata;
/**
 * Returns An object with the action handlers defined in the metrics of the asset type.
 *
 * @returns An object with the functions used to execute an action
 */
const usePortalMetricsActionsComponents = () => {
    const assetPortalConfiguration = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    const metadata = react_1.default.useMemo(() => { var _a, _b; return (_b = (_a = assetPortalConfiguration === null || assetPortalConfiguration === void 0 ? void 0 : assetPortalConfiguration.metrics) === null || _a === void 0 ? void 0 : _a.actionsComponents) !== null && _b !== void 0 ? _b : null; }, [assetPortalConfiguration]);
    return metadata;
};
exports.usePortalMetricsActionsComponents = usePortalMetricsActionsComponents;
/**
 * Returns a route portal flag isDraft
 *
 * @example
 *
 * ```ts
 * import { useIsPortalDraft } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = ({ ...props }) => {
 *    const isDraft = useIsPortalDraft();
 *
 *   <MyComponent display-if={isDraft}/>
 *   ...
 * };
 * ```
 *
 * @returns A boolean that will be true if the user is navigating a draft
 */
const useIsPortalDraft = () => {
    const { isDraft } = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    return isDraft;
};
exports.useIsPortalDraft = useIsPortalDraft;
/**
 * Returns a route portal flag isEdit
 *
 * @example
 *
 * ```ts
 * import { useIsPortalEdit } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = ({ ...props }) => {
 *    const isEdit = useIsPortalEdit();
 *
 *   <MyComponent display-if={isEdit}/>
 *   ...
 * };
 * ```
 *
 * @returns A boolean that will be true if the user is editing the portal
 */
const useIsPortalEdit = () => {
    const { isEdit } = react_1.default.useContext(withAssetPortal_1.AssetPortalContext);
    return isEdit;
};
exports.useIsPortalEdit = useIsPortalEdit;
